import { DependencyList, useEffect } from 'react';
import { isFunction } from 'lodash/fp';

/**
 * Delacon is a call tracking and analytics service that tracks phone calls from the website.
 * It dynamically inserts tracking phone numbers on the page and monitors call conversions.
 */

export type WindowWithDelacon = typeof window & {
  placids?: string;
  plaPageReload?: ((refCids: string) => void) | 'undefined';
};

export const DELACON = {
  companyId: '83086',
};

/**
 * Initializes the Delacon tracking script by injecting it into the page.
 * This should be called once when the application loads.
 * @param {boolean} enabled - Whether the Delacon script should be injected.
 */
const useDelaconScript = (enabled = true) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = '//vxml4.plavxml.com/sited/ref/ctrk/670-83086';

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [enabled]);
};

/**
 * Reloads the Delacon script to refresh tracking phone numbers on the page.
 * This is necessary for Single Page Applications (SPAs) where page content changes
 * without a full page reload. Delacon has a ~20 second timeout, therefore this should be
 * called when new contact information is rendered.
 *
 * Link: https://support.delaconcorp.com/hc/en-us/articles/360039026414-Reload-Code-for-Single-Page-Applications.
 *
 * @param {DependencyList} deps - UseEffect dependency array.
 *
 */
export const useReloadDelaconScript = (deps: DependencyList = []) => {
  useEffect(() => {
    const reloadDelacon = (window as WindowWithDelacon)?.plaPageReload;
    const delaconPlacids = (window as WindowWithDelacon)?.placids;
    if (delaconPlacids && isFunction(reloadDelacon)) {
      // Check `placids` exists to prevent race condition errors during page initialization.
      // If `placids` is undefined when Delacon reloads, their script will throw an error and crash the page.
      // This can occur when multiple components attempt to reload Delacon simultaneously.
      reloadDelacon(DELACON.companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default useDelaconScript;
